import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { MdCheck, MdClose, MdLinkOff } from 'react-icons/md';

import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import Input from '@/components/Input';
import { Tag, type TagColor } from '@/components/Tag';
import Loading from '@/components/Loading';
import Select from '@/components/Select';
import MultiSelect, { type Option as MultiSelectOption } from '@/components/MultiSelect';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { AgentApiCandidateProfilesItem } from '@/types/serializers';
import type { ActiveUser } from '@/features/agent/types/ActiveUser';
import type { CandidateRecipient } from '../lib/recipients';

interface CandidateRecipientSelectModalProps {
  open: boolean;
  onClose: () => void;
  searchKeyword: string;
  onSearchKeywordChange: Dispatch<SetStateAction<string>>;
  candidates: AgentApiCandidateProfilesItem[];
  selectedRecipients: CandidateRecipient[];
  onToggleRecipient: (candidate: AgentApiCandidateProfilesItem) => void;
  onClearSelections: () => void;
  onSelectAllVisible: () => void;
  isLoading: boolean;
  isRefreshing: boolean;
  errorMessage: string | null;
  isEmpty: boolean;
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  totalCount: number;
  organizationUsers: ActiveUser[];
  selectedOrganizationUserId: number | null;
  onSelectedOrganizationUserIdChange: Dispatch<SetStateAction<number | null>>;
  tagOptions: MultiSelectOption[];
  selectedTagIds: number[];
  onSelectedTagIdsChange: Dispatch<SetStateAction<number[]>>;
}

export default function CandidateRecipientSelectModal({
  open,
  onClose,
  searchKeyword,
  onSearchKeywordChange,
  candidates,
  selectedRecipients,
  onToggleRecipient,
  onClearSelections,
  onSelectAllVisible,
  isLoading,
  isRefreshing,
  errorMessage,
  isEmpty,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  organizationUsers,
  selectedOrganizationUserId,
  onSelectedOrganizationUserIdChange,
  tagOptions,
  selectedTagIds,
  onSelectedTagIdsChange,
}: CandidateRecipientSelectModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasActiveFilters =
    searchKeyword.trim().length > 0 ||
    selectedOrganizationUserId !== null ||
    selectedTagIds.length > 0;
  const organizationUserSelectItems = [
    { value: 'all', label: '全ての担当者' },
    ...organizationUsers.map((user) => ({
      value: String(user.id),
      label: user.name,
    })),
  ];
  const organizationUserSelectValue =
    selectedOrganizationUserId === null ? 'all' : String(selectedOrganizationUserId);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [open]);

  useEffect(() => {
    if (!hasNextPage) return;
    const sentinel = sentinelRef.current;
    const root = listContainerRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (isLoading || isFetchingNextPage || isRefreshing) return;
        onLoadMore();
      },
      {
        root,
        rootMargin: '0px 0px 120px 0px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isLoading, isFetchingNextPage, isRefreshing, onLoadMore]);

  const handleClearFilters = () => {
    onSearchKeywordChange('');
    onSelectedOrganizationUserIdChange(null);
    onSelectedTagIdsChange([]);
  };

  const renderContent = () => {
    if (isLoading && candidates.length === 0) {
      return (
        <div className="flex h-[160px] items-center justify-center">
          <Loading size="small" />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="flex h-[160px] items-center justify-center px-md">
          <span className="text-xs text-danger">{errorMessage}</span>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="flex h-[160px] items-center justify-center px-md">
          <span className="text-xs text-sub">該当する求職者が見つかりませんでした。</span>
        </div>
      );
    }

    return (
      <ul className="flex flex-col">
        {candidates.map((candidate) => {
          const isSelected = selectedRecipients.some((recipient) => recipient.id === candidate.id);
          const organizationUsers = candidate.organization_users ?? [];
          const firstOrganizationUserName = organizationUsers[0]?.name ?? null;
          const extraOrganizationUserCount = Math.max(0, organizationUsers.length - 1);
          const candidateTags = candidate.tags ?? [];
          const email = candidate.email ?? null;

          return (
            <li
              key={candidate.id}
              className="group flex cursor-pointer items-center justify-between gap-md border-b border-solid border-border px-sm py-xs hover:bg-gray"
              onClick={() => onToggleRecipient(candidate)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleRecipient(candidate);
                }
              }}
            >
              <div className="flex min-w-0 items-center gap-md">
                <div onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    value={candidate.id}
                    checked={isSelected}
                    onChange={() => onToggleRecipient(candidate)}
                  />
                </div>
                <div className="flex min-w-0 flex-col">
                  <div className="flex items-center gap-xs">
                    <span className="text-heading-xxs text-main">
                      {candidate.name}
                      {candidate.age !== null ? `（${candidate.age}）` : ''}
                    </span>
                    {firstOrganizationUserName && (
                      <Tag color="default" size="sm" className="text-main">
                        {firstOrganizationUserName}
                        {extraOrganizationUserCount > 0 && ` ＋${extraOrganizationUserCount}`}
                      </Tag>
                    )}
                  </div>
                  {email && <span className="truncate text-body-xs text-sub">{email}</span>}
                </div>
              </div>
              {candidateTags.length > 0 && (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-xs">
                  {candidateTags.map((tag) => (
                    <Tag key={tag.id} color={tag.color_hex as TagColor} size="sm">
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="flex h-[742px] max-h-[90vh] w-[978px] max-w-[91vw] flex-col overflow-hidden rounded-sm shadow-layer3!">
        <div className="shrink-0 border-b border-solid border-border px-lg pt-md pb-sm">
          <DialogTitle className="text-heading-xs! text-main">送信先の追加</DialogTitle>
          <DialogDescription className="sr-only">送信先の求職者を選択します</DialogDescription>
        </div>

        <div className="flex shrink-0 flex-col gap-xs px-lg pt-md pb-xs">
          <div className="flex items-end gap-md">
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label htmlFor="candidate-recipient-search" className="text-heading-xxs text-main">
                フリーワード
              </label>
              <Input
                id="candidate-recipient-search"
                ref={inputRef}
                className="w-full"
                placeholder="名前や会社名を入力して検索"
                autoComplete="off"
                search
                value={searchKeyword}
                onChange={(event) => onSearchKeywordChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
            </div>
            <div className="flex w-[286px] shrink-0 flex-col gap-xs">
              <label
                className="text-heading-xxs text-main"
                htmlFor="candidate-recipient-organization-user"
              >
                担当者
              </label>
              <Select
                id="candidate-recipient-organization-user"
                className="w-full"
                items={organizationUserSelectItems}
                defaultLabel="全ての担当者"
                value={organizationUserSelectValue}
                onValueChange={(value) => {
                  if (value === 'all') {
                    onSelectedOrganizationUserIdChange(null);
                    return;
                  }
                  onSelectedOrganizationUserIdChange(Number(value));
                }}
              />
            </div>
            <div className="flex w-[236px] shrink-0 flex-col gap-xs">
              <span className="text-heading-xxs text-main">タグ</span>
              <MultiSelect
                options={tagOptions}
                value={selectedTagIds}
                onChange={(value) => {
                  onSelectedTagIdsChange(value);
                }}
                placeholder="タグを選択"
                matchTriggerWidth
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="flex h-[42px] cursor-pointer items-center gap-xxs rounded-sm border-none bg-transparent px-sm text-button-md text-primary transition hover:bg-primary_lighten disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onSelectAllVisible}
              disabled={candidates.length === 0}
            >
              <MdCheck className="size-5" />
              表示中を全て選択
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                className="flex h-[42px] cursor-pointer items-center gap-xxs rounded-sm border-none bg-transparent px-sm text-button-md text-primary transition hover:bg-primary_lighten"
                onClick={handleClearFilters}
              >
                <MdClose className="size-5" />
                検索条件をクリア
              </button>
            )}
          </div>
        </div>

        <div
          ref={listContainerRef}
          className="hidden-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-lg"
        >
          {renderContent()}
          <div ref={sentinelRef} className="h-4" />
          {isFetchingNextPage && (
            <div className="flex w-full justify-center pb-xs">
              <Loading size="small" />
            </div>
          )}
          {isRefreshing && !isLoading && (
            <div className="flex w-full justify-center py-xs">
              <span className="text-xs text-sub">更新しています...</span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-md border-t border-solid border-border px-lg py-md">
          {selectedRecipients.length > 0 ? (
            <div className="flex items-center gap-xs">
              <span className="flex items-center gap-[3px] pr-md text-main">
                <span className="text-heading-md">{selectedRecipients.length}件</span>
                <span className="text-body-xs">選択中</span>
              </span>
              <Button type="button" color="secondary" size="md" onClick={onClearSelections}>
                <MdLinkOff className="size-5" />
                選択解除
              </Button>
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-xs">
            <Button type="button" color="secondary" size="md" onClick={onClose}>
              キャンセル
            </Button>
            <Button type="button" color="primary" size="md" onClick={onClose}>
              完了
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
